/*
 * Copyright (c) 2021 Mike Vo (info@miketvo.com) - All Rights Reserved.
 *
 * This file is part of the project "miketvo-website".
 *
 * This project is an online personal website by Mike Vo.
 *
 * COPYING AND/OR DISTRIBUTING OF ANY CONTENT WITHIN THIS PROJECT WITHOUT EX-
 * PRESS PERMISSION FROM THE CLIENT AND/OR THE AUTHOR, VIA ANY MEDIUM IS
 * STRICTLY PROHIBITED.
 *
 * Written by Mike Vo on June 27th 2021.
 *
 */

import * as React from "react";
import AniLink from "gatsby-plugin-transition-link/AniLink";
import { StaticImage } from "gatsby-plugin-image";
import scrollTo from "gatsby-plugin-smoothscroll";

import Layout from "../../components/layout";
import Seo from "../../components/seo";
import SetLang from "../../components/setLang";
import * as headingStyle from "../../styles/heading.module.css";
import * as style from "../styles/index.module.css";

const IndexPageVI = () => (
  <Layout lang="vi">
    <Seo title="Thông tin" lang="vi" />
    <SetLang lang="vi" />
    <div className={style.mainWrapper}>
      <section className={[style.section, style.about].join(" ")}>
        <div data-sal="fade" data-sal-duration="1000" className={style.profilePictureFrameOuter}>
          <div className={style.profilePictureFrameInner}>
            <div className={style.profilePicture}>
              <StaticImage
                className={style.img}
                src="../images/portrait.jpg"
                width={300}
                quality={95}
                formats={["AUTO", "WEBP", "AVIF"]}
                alt="Portrait photo"
              />
            </div>
          </div>
        </div>
        <div className={style.contentWrapper}>
          <div
            data-sal="slide-left"
            data-sal-duration="1000"
            className={[headingStyle.dash, headingStyle.gradientPurpleBlue].join(" ")}
          />
          <h1 data-sal="slide-left" data-sal-duration="1000" data-sal-delay="200" className={headingStyle.heading}>
            Xin chào, mình là Mike.
          </h1>
          <p data-sal="slide-left" data-sal-duration="1000" data-sal-delay="400">
            Mình đang là sinh viên tại Viện Công nghệ Hoàng gia Melbourne (RMIT) và là kỹ sư thiết kế - phát triển giải
            pháp phần mềm mạng. Mình chuyên về xây dựng website trên nền tảng JAMstack với tốc độ và độ ổn định cao, đảm
            bảo truyền tải thương hiệu khách hàng, đồng thời chú trọng vào trải nghiệm người dùng, tính thẩm mỹ, toàn
            cầu hóa và tối ưu hóa công cụ tìm kiếm (SEO).
          </p>
          <p data-sal="slide-left" data-sal-duration="1000" data-sal-delay="600">
            Mình nhận xây dựng website theo yêu cầu. Xem phần{" "}
            <span className={"animated-anchor"}>
              <AniLink cover direction="up" duration={1} bg="var(--light-black)" to={"/vi/services/"}>
                Dịch vụ
              </AniLink>
            </span>{" "}
            để biết thêm chi tiết.
          </p>
          <p data-sal="slide-left" data-sal-duration="1000" data-sal-delay="800">
            Sở thích: Chơi nhạc, viết game pixel art, uống trà và chơi với mèo.
          </p>
          <div data-sal="slide-left" data-sal-duration="1000" data-sal-delay="1000" className={style.buttonBox}>
            <div className={style.buttonWrapper}>
              <button
                className={style.readMoreButton}
                onClick={() => {
                  scrollTo("#first-scroll");
                }}
              >
                <div className={style.before} />
                <div className={style.after} />
                <div className={style.content}>
                  <span>Đọc thêm</span>
                </div>
              </button>
              <img
                src={"/media/south_white_24dp.svg"}
                alt={""}
                onClick={() => {
                  scrollTo("#first-scroll");
                }}
                onKeyDown={() => {
                  scrollTo("#first-scroll");
                }}
              />
            </div>
            <div className={style.buttonWrapper}>
              <AniLink cover direction="up" duration={1} bg="var(--light-black)" to={"/vi/contact/"}>
                <button className={style.contactButton}>
                  <div className={style.before} />
                  <div className={style.after} />
                  <div className={style.content}>
                    <span>Liên hệ</span>
                  </div>
                </button>
              </AniLink>
            </div>
          </div>
        </div>
      </section>
      <section id="first-scroll" className={[style.section, style.expertise].join(" ")}>
        <div className={style.contentWrapper}>
          <div
            data-sal="slide-down"
            data-sal-duration="1000"
            data-sal-delay="150"
            className={[headingStyle.dash, headingStyle.solidWhite].join(" ")}
          />
          <h1 data-sal="slide-down" data-sal-duration="1000" data-sal-delay="300" className={headingStyle.heading}>
            Chuyên môn
          </h1>
          <ul className={style.list}>
            <li>Javascript ES6</li>
            <li>PostCSS</li>
            <li>ExpressJS</li>
            <li>ReactJS</li>
            <li>SQL</li>
            <li>NoSQL</li>
            <li>GraphQL</li>
            <li>Vẽ đồ họa</li>
            <li>Photoshop</li>
            <li>Thiết kế UI/UX</li>
            <li>Hoạt hình 2D</li>
            <li>Pixel Art</li>
          </ul>
        </div>
      </section>
      <section className={[style.section, style.technologies].join(" ")}>
        <div className={style.contentWrapper}>
          <div
            data-sal="slide-down"
            data-sal-duration="1000"
            data-sal-delay="450"
            className={[headingStyle.dash, headingStyle.solidBlack].join(" ")}
          />
          <h1 data-sal="slide-down" data-sal-duration="1000" data-sal-delay="600" className={headingStyle.heading}>
            Giải pháp Công nghệ
          </h1>
          <ul className={style.list}>
            <li>
              <img src={"/media/Netlify_CMS_logo.svg"} alt={"Netlify CMS"} />
            </li>
            <li>
              <img src={"/media/netlify_wordmark.svg"} alt={"Netlify"} />
            </li>
            <li>
              <img src={"/media/Wordpress.svg"} alt={"Wordpress"} />
            </li>
            <li>
              <img src={"/media/Amazon_Web_Services_Logo.svg"} alt={"Amazon Web Services (AWS)"} />
            </li>
            <li>
              <img src={"/media/nodejs.svg"} alt={"NodeJS"} />
            </li>
            <li>
              <img src={"/media/PostgreSQL-Logo.svg"} alt={"PostgreSQL"} />
            </li>
            <li>
              <img src={"/media/MongoDB_Logo_FullColorBlack_RGB.svg"} alt={"MongoDB"} />
            </li>
            <li>
              <img src={"/media/Gatsby.svg"} alt={"Gatsby React Framework"} />
            </li>
            <li>
              <img src={"/media/Black-Next.js.svg"} alt={"Next.JS"} />
            </li>
          </ul>
        </div>
      </section>
    </div>
  </Layout>
);

export default IndexPageVI;
